import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Loader2, ShieldCheck, Landmark, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicInvoicePay() {
  const { invoice_id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!invoice_id || !token) {
      setError("Invalid or missing payment link.");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/public/invoice/${invoice_id}`, { params: { token } })
      .then((res) => {
        setInvoice(res.data);
      })
      .catch((err) => setError(err.response?.data?.detail || "Payment link is invalid or expired"))
      .finally(() => setLoading(false));
  }, [invoice_id, token]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/public/invoice/${invoice_id}/pay`,
        { payment_method: "card", language: "en", origin_url: window.location.origin },
        { params: { token } }
      );
      if (res.data.processor === "fiserv" && res.data.gateway_url && res.data.form_params) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = res.data.gateway_url;
        Object.entries(res.data.form_params).forEach(([key, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment initiation failed");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-3" />
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Payment Link Invalid</h2>
            <p className="text-slate-600 text-sm">{error}</p>
            <p className="text-slate-400 text-xs mt-4">Please contact <a href="mailto:accounts@sebasalliance.net" className="underline">accounts@sebasalliance.net</a> for a fresh payment link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invoice.status === "paid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500 mb-3" />
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Invoice Already Paid</h2>
            <p className="text-slate-600 text-sm">{invoice.invoice_number} • ${(invoice.total || 0).toFixed(2)} {invoice.currency}</p>
            <p className="text-slate-400 text-xs mt-4">Thank you for your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Sebastian Alliance Group</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Pay Your Invoice</h1>
        </div>

        <Card className="mb-4">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-base font-heading">{invoice.invoice_number}</CardTitle>
              <Badge variant="outline" className="uppercase text-[10px]">{invoice.status}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Billed to</span>
              <span className="text-sm font-medium text-slate-800">{invoice.client_name}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Period</span>
              <span className="text-sm text-slate-800">{invoice.billing_period_start} → {invoice.billing_period_end}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Due date</span>
              <span className="text-sm font-medium text-slate-800">{invoice.due_date}</span>
            </div>
            <div className="flex justify-between items-center pt-3">
              <span className="text-sm text-slate-500">Amount due</span>
              <span className="text-2xl font-bold text-slate-900">${(invoice.total || 0).toFixed(2)} <span className="text-sm text-slate-500">{invoice.currency}</span></span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Choose Payment Method</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="w-full p-4 rounded-lg border-2 border-blue-200 bg-blue-50" data-testid="public-configured-processor">
              {invoice.payment_processor === "fiserv" ? (
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    <Landmark className="h-4 w-4 text-emerald-700" /> Fiserv IPG (Scotiabank)
                    <Badge className="bg-slate-100 text-slate-700 text-[10px]">Configured</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">This invoice is configured for Bahamas card payment in BSD.</div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-blue-600" /> Stripe
                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">Configured</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Secure card payment using the processor configured for this invoice.</div>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 text-center">The payment processor is locked to the invoice configuration for accurate reconciliation.</p>

            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mt-4 py-6 text-base"
              onClick={handlePay}
              disabled={paying}
              data-testid="public-pay-btn"
            >
              {paying ? (
                <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Redirecting…</>
              ) : (
                <><CreditCard className="h-5 w-5 mr-2" /> Pay ${(invoice.total || 0).toFixed(2)} {invoice.currency}</>
              )}
            </Button>

            <div className="pt-3 text-xs text-slate-500 text-center flex items-center justify-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5" /> Secure payment • No login required
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          Questions? Contact <a href="mailto:accounts@sebasalliance.net" className="underline">accounts@sebasalliance.net</a>
        </p>
      </div>
    </div>
  );
}
